import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useState } from "react"
import * as yup from "yup"

import DialogProvider from "@components/dialog_provider"
import { useCustomerPageContext } from "@contexts/customer"
import { customersDeleteUser } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack, Typography } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import { CustomerPerson } from "__generated__/graphql"

export type UserFormData = {
  isApproved: boolean
}

export default function DeleteCustomerUser({
  isOpen,
  setClose,
  selectedRow,
  setTotalUserCount
}: {
  isOpen: boolean
  selectedRow?: CustomerPerson
  setClose: () => void
  setTotalUserCount: React.Dispatch<React.SetStateAction<number>>
}) {
  const { translate, setUsers, users } = useCustomerPageContext()

  const schema = yup
    .object({
      isApproved: yup.boolean().required()
    })
    .required()

  const {
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<UserFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      isApproved: false
    }
  })

  useEffect(() => {
    if (selectedRow) {
      reset({
        isApproved: false
      })
    }
  }, [selectedRow])

  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
    reset()
  }

  async function onSubmit() {
    if (!selectedRow?._id) {
      return
    }

    setLoading(true)
    const { errorMessage, isSuccess } = await customersDeleteUser({
      _id: selectedRow?._id,
      customerId: selectedRow.customer
    })
    if (!isSuccess) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return
    }
    const totalCount = users.length - 1
    setUsers?.(
      users.filter((user) => {
        if (user._id === selectedRow?._id) {
          return false
        }
        return true
      })
    )
    setTotalUserCount(totalCount)
    queryClient.invalidateQueries({ queryKey: ["customer-data-table"] })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("delete_user")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack justifyContent={"center"} alignItems={"center"} flex={1}>
        <Typography align="center">
          {translate("are_you_sure_deleting_customer_person")}
        </Typography>
      </Stack>
    </DialogProvider>
  )
}
